import { useState, useEffect, useCallback } from 'react'
import Boton from '../components/Boton'
import { conciliacionTarjetasApi as api } from '../api/conciliacionTarjetas'
import { fmtFecha, fmtFechaHora } from '../utils/fecha'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * FINANZAS → Conciliación de tarjetas (Martin, 28 sep 2026).
 *
 * Se cargan los dos archivos de Payway (movimientos = "Liquidadas", y pagos).
 * LGR baja de Presea tarjetas_TODAS.DBI, cruza por pasadas, y arma CONS_TARJ
 * solo con las liquidaciones completas (cada una con su número LGR de 10
 * dígitos). El informe de cargos e impuestos por marca usa las cifras exactas
 * de Pagos de esas liquidaciones.
 */

const $ = (n) => (n == null ? '—' : Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const ESTADO = {
  procesando: { label: 'Procesando', bg: colors.warnBg, color: colors.warn },
  error: { label: 'Error', bg: colors.errBg, color: colors.err },
  generada: { label: 'Generada', bg: colors.bgSoft, color: colors.textSec },
  subida: { label: 'En el FTP', bg: colors.linkBg, color: colors.link },
}

export default function ConciliacionTarjetasPage() {
  const [lista, setLista] = useState([])
  const [sel, setSel] = useState(null)
  const [det, setDet] = useState(null)
  const [tab, setTab] = useState('informe')
  const [liq, setLiq] = useState(null)
  const [pag, setPag] = useState(null)
  const [ocupado, setOcupado] = useState(null)
  const [error, setError] = useState(null)
  const [aviso, setAviso] = useState(null)

  const cargarLista = useCallback(() => api.listar().then(({ data }) => setLista(data)), [])
  useEffect(() => { cargarLista() }, [cargarLista])
  useEffect(() => {
    if (!sel) return
    api.ver(sel).then(({ data }) => setDet(data)).catch(() => setError('No se pudo abrir la conciliación.'))
  }, [sel])

  // Mientras corre en segundo plano (baja el export de Presea y cruza), se consulta cada 5 s.
  const enCurso = det?.conciliacion?.tarea
  useEffect(() => {
    if (!enCurso || !sel) return
    const t = setInterval(() => {
      api.ver(sel).then(({ data }) => {
        setDet(data)
        if (!data.conciliacion.tarea) cargarLista()
      }).catch(() => {})
    }, 5000)
    return () => clearInterval(t)
  }, [enCurso, sel, cargarLista])

  const fallo = (e, txt) => {
    const errs = e.response?.data?.errors
    setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || txt))
  }
  const avisar = (t) => { setAviso(t); setTimeout(() => setAviso(null), 4000) }

  const conciliar = async () => {
    if (!liq || !pag) return
    setOcupado('conciliar'); setError(null)
    try {
      const { data } = await api.conciliar(liq, pag)
      setDet(data); setSel(data.conciliacion.id); setTab('informe')
      setLiq(null); setPag(null)
      document.getElementById('ct-liq').value = ''
      document.getElementById('ct-pag').value = ''
      await cargarLista()
    } catch (e) { fallo(e, 'No se pudo conciliar.') } finally { setOcupado(null) }
  }

  const accion = async (nombre, fn, ok) => {
    setOcupado(nombre); setError(null)
    try {
      const { data } = await fn()
      if (data?.conciliacion) setDet(data)
      if (ok) avisar(ok)
      await cargarLista()
    } catch (e) { fallo(e, 'No se pudo completar la acción.') } finally { setOcupado(null) }
  }

  const c = det?.conciliacion
  const sinPareja = det?.sin_pareja || []

  return (
    <div style={st.wrap}>
      <div style={st.head}>
        <h1 style={st.h1}>Conciliación de tarjetas</h1>
        <span style={st.sub}>Payway contra los movimientos de tarjeta de Presea</span>
      </div>

      {error && <div style={st.error} role="alert">{error}</div>}
      {aviso && <div style={st.aviso}>{aviso}</div>}

      <div style={{ ...sx.card, marginBottom: space.xl }}>
        <h3 style={sx.cardTitle}>Nueva conciliación</h3>
        <div style={st.carga}>
          <label style={st.campo} htmlFor="ct-liq">
            <span style={st.label}>Movimientos de Payway (Liquidadas)</span>
            <input id="ct-liq" type="file" accept=".csv,text/csv" onChange={(e) => setLiq(e.target.files?.[0] || null)} />
          </label>
          <label style={st.campo} htmlFor="ct-pag">
            <span style={st.label}>Pagos de Payway</span>
            <input id="ct-pag" type="file" accept=".csv,text/csv" onChange={(e) => setPag(e.target.files?.[0] || null)} />
          </label>
          <Boton variante="primary" disabled={!liq || !pag || ocupado !== null} onClick={conciliar}>
            {ocupado === 'conciliar' ? 'Cargando…' : 'Conciliar'}
          </Boton>
        </div>
        <p style={st.nota}>Al conciliar, LGR baja de Presea el export de movimientos de tarjeta (tarjetas_TODAS.DBI) y cruza los cupones de a pasadas, en segundo plano. Nada se manda a Presea hasta que aprietes "Subir al FTP".</p>
      </div>

      <div style={st.cuerpo}>
        <div style={{ ...sx.card, padding: 0, alignSelf: 'start' }}>
          <div style={{ padding: `${space.lg}px ${space.xl}px` }}><h3 style={sx.cardTitle}>Conciliaciones</h3></div>
          {lista.length === 0 ? <div style={st.vacio}>Todavía no hay conciliaciones.</div> : lista.map((x) => (
            <button key={x.id} type="button" onClick={() => setSel(x.id)} style={{ ...st.fila, ...(sel === x.id ? st.filaSel : {}) }}>
              <div style={st.filaTop}>
                <span style={sx.mono}>#{x.id}</span>
                <span style={{ ...sx.badge, background: ESTADO[x.estado]?.bg, color: ESTADO[x.estado]?.color }}>{ESTADO[x.estado]?.label || x.estado}</span>
              </div>
              <div style={st.filaMeta}>{fmtFechaHora(x.created_at)}</div>
              <div style={st.filaMeta}>{x.estado === 'procesando' ? 'Procesando…' : x.estado === 'error' ? 'No se pudo conciliar' : `${x.apareados}/${x.cupones - (x.ya_conciliados || 0)} cupones · ${x.liquidaciones_completas} liq. completas`}</div>
            </button>
          ))}
        </div>

        {!c ? <div style={{ ...sx.card, ...st.vacio }}>Elegí una conciliación o cargá los archivos.</div> : c.estado === 'procesando' || c.estado === 'error' ? (
          <div style={sx.card}>
            <h2 style={st.h2}>Conciliación #{c.id}</h2>
            <div style={st.filaMeta}>{c.archivo_liquidadas} · {c.archivo_pagos}</div>
            {c.estado === 'procesando'
              ? <p style={st.procesando}>Procesando: LGR está bajando el export de Presea y cruzando los cupones. Tarda uno o dos minutos; esta pantalla se actualiza sola.</p>
              : <div style={{ ...st.error, marginTop: space.lg }} role="alert">No se pudo conciliar: {c.error}</div>}
          </div>
        ) : (
          <div style={{ minWidth: 0 }}>
            <div style={{ ...sx.card, marginBottom: space.lg }}>
              <div style={st.detHead}>
                <div>
                  <h2 style={st.h2}>Conciliación #{c.id}</h2>
                  <div style={st.filaMeta}>{fmtFechaHora(c.created_at)}{c.usuario ? ` · ${c.usuario.nombre}` : ''} · {c.archivo_liquidadas} · {c.archivo_pagos}</div>
                  {c.subida_at && <div style={st.filaMeta}>Subida al FTP: {fmtFechaHora(c.subida_at)}{c.verificada_at ? ` · verificada en Presea: ${fmtFechaHora(c.verificada_at)}` : ''}</div>}
                </div>
                <div style={st.acciones}>
                  <Boton sm variante="ghost" onClick={() => api.bajarArchivo(c.id, det.archivo)}>Bajar {det.archivo}</Boton>
                  <Boton sm variante="ghost" onClick={() => api.bajarInformePdf(c.id, c.numero_lgr)}>Informe (PDF)</Boton>
                  <Boton sm variante="ghost" onClick={() => api.bajarInforme(c.id)}>Informe (CSV)</Boton>
                  {sinPareja.length > 0 && <Boton sm variante="ghost" onClick={() => api.bajarSinPareja(c.id)}>Sin pareja (CSV)</Boton>}
                  <Boton sm variante="send" disabled={ocupado !== null || c.cupones_en_archivo === 0}
                    title="Deja el archivo en la carpeta del FTP de Presea"
                    onClick={() => accion('ftp', () => api.subirFtp(c.id), `${det.archivo} quedó en el FTP de Presea.`)}>
                    {ocupado === 'ftp' ? '…' : (c.subida_at ? '→ Volver a subir al FTP' : '→ Subir al FTP de Presea')}
                  </Boton>
                  {c.subida_at && (
                    <Boton sm variante="secondary" disabled={ocupado !== null || !!c.tarea}
                      title="Vuelve a bajar el export de Presea y marca qué movimientos tomó"
                      onClick={() => accion('verificar', () => api.verificar(c.id), 'Verificación hecha: el informe muestra solo lo que Presea tomó.')}>
                      {ocupado === 'verificar' ? '…' : 'Verificar en Presea'}
                    </Boton>
                  )}
                </div>
              </div>

              {c.aviso && <div style={{ ...st.error, marginTop: space.lg }} role="alert">{c.aviso}</div>}
              {c.tarea === 'verificar' && <p style={st.procesando}>Verificando en Presea: LGR vuelve a bajar el export y marca qué movimientos tomó. Se actualiza sola.</p>}
              {c.error && !c.tarea && <div style={{ ...st.error, marginTop: space.lg }} role="alert">La última verificación falló: {c.error}</div>}

              <div style={st.numeros}>
                <Numero n={c.cupones} label="Cupones de Payway" />
                <Numero n={c.ya_conciliados || 0} label="Ya conciliados en Presea (descartados)" />
                <Numero n={c.apareados} label="Encontrados en Presea" />
                <Numero n={sinPareja.length} label="Sin pareja" alerta={sinPareja.length > 0} />
                <Numero n={`${c.liquidaciones_completas}/${c.liquidaciones}`} label="Liquidaciones completas" />
                <Numero n={c.cupones_en_archivo} label={`Van al archivo · $ ${$(c.bruto_en_archivo)}`} />
              </div>
            </div>

            <div style={st.tabs} role="tablist">
              {[['informe', 'Cargos e impuestos por marca'], ['liquidaciones', `Liquidaciones (${det.liquidaciones.length})`], ['sin', `Sin pareja (${sinPareja.length})`], ['pasadas', 'Pasadas del cruce']].map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={{ ...st.tab, ...(tab === k ? st.tabOn : {}) }}>{l}</button>
              ))}
            </div>

            <div style={{ ...sx.card, padding: 0, overflowX: 'auto' }}>
              {tab === 'informe' && <Informe inf={det.informe} />}
              {tab === 'liquidaciones' && <Liquidaciones filas={det.liquidaciones} verificada={!!c.verificada_at} />}
              {tab === 'sin' && <SinPareja filas={sinPareja} />}
              {tab === 'pasadas' && <Pasadas filas={det.pasadas} />}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Numero({ n, label, alerta = false }) {
  return (
    <div style={st.numero}>
      <div style={{ ...st.numeroN, ...(alerta ? { color: colors.warnStrong } : {}) }}>{n}</div>
      <div style={st.numeroL}>{label}</div>
    </div>
  )
}

function Informe({ inf }) {
  const marcas = Object.entries(inf.por_marca || {})
  if (marcas.length === 0) return <div style={st.vacio}>No hay liquidaciones completas{inf.verificado ? ' que Presea haya tomado' : ''}: el informe queda vacío.</div>
  const cols = inf.conceptos
  const tot = (x) => Object.values(x.cargos || {}).reduce((a, b) => a + b, 0)
  return (
    <div>
      <p style={{ ...st.nota, padding: `${space.lg}px ${space.xl}px`, margin: 0 }}>
        Impuestos de los cupones que van en el archivo a Presea{inf.verificado ? ' y que Presea tomó' : ''}, por marca de tarjeta. Payway los informa por liquidación: si la liquidación entra entera y es de una sola marca, van tal cual; si entra en parte o mezcla marcas, se calculan por cupón (arancel y costo de cuotas reales, IVA e IIBB con sus fórmulas, comprobadas contra la liquidación entera).
      </p>
      {(() => {
        const n = inf.total.liquidaciones
        const mal = inf.no_verificadas || []
        return mal.length === 0
          ? <div style={{ ...st.aviso, margin: `0 ${space.xl}px ${space.md}px` }}>Controles: las {n} liquidaciones cierran con sus fórmulas (IVA 21 % del arancel, IVA 21 % o 10,5 % del costo de cuotas, IVA especial 3 %, IIBB sobre su base, bruto − neto = cargos).</div>
          : <div style={{ ...st.error, margin: `0 ${space.xl}px ${space.md}px` }} role="alert">
              {mal.length} de {n} liquidaciones NO cierran con sus fórmulas. No usar estos importes sin revisarlas:
              {mal.map((l) => <div key={`${l.nro_liq}-${l.num_est}`}>Liq. {l.nro_liq} · est. {l.num_est}: {l.controles.join(' · ')}</div>)}
            </div>
      })()}
      {inf.con_cupones_previos?.length > 0 && (
        <div style={{ ...st.aviso, margin: `0 ${space.xl}px ${space.md}px` }}>
          {inf.con_cupones_previos.length} liquidaciones tienen cupones que ya estaban conciliados en Presea: acá van solo los impuestos de sus cupones de este archivo, calculados por cupón.
          {inf.con_cupones_previos.map((l) => <div key={`${l.nro_liq}-${l.num_est}`}>Liq. {l.nro_liq} · est. {l.num_est}: {l.cupones_ya_conciliados} de {l.cupones + l.cupones_ya_conciliados} cupones ya conciliados · bruto $ {$(l.bruto)}</div>)}
        </div>
      )}
      <table style={sx.table}>
        <thead>
          <tr>
            <th style={sx.th}>Marca</th>
            <th style={{ ...sx.th, textAlign: 'right' }}>Liq.</th>
            <th style={{ ...sx.th, textAlign: 'right' }}>Cupones</th>
            <th style={{ ...sx.th, textAlign: 'right' }}>Bruto</th>
            {cols.map((k) => <th key={k.columna} style={{ ...sx.th, textAlign: 'right' }} title={k.grupo}>{k.nombre}</th>)}
            <th style={{ ...sx.th, textAlign: 'right' }}>Total descontado</th>
            <th style={{ ...sx.th, textAlign: 'right' }}>Neto</th>
          </tr>
        </thead>
        <tbody>
          {[...marcas, ['TOTAL', inf.total]].map(([m, x]) => (
            <tr key={m} style={m === 'TOTAL' ? st.trTotal : st.tr}>
              <td style={{ ...st.td, fontWeight: 600 }}>{m}</td>
              <td style={st.tdNum}>{x.liquidaciones}</td>
              <td style={st.tdNum}>{x.cupones}</td>
              <td style={st.tdNum}>{$(x.bruto)}</td>
              {cols.map((k) => <td key={k.columna} style={st.tdNum}>{$(x.cargos?.[k.columna] || 0)}</td>)}
              <td style={{ ...st.tdNum, fontWeight: 600 }}>{$(tot(x))}</td>
              <td style={st.tdNum}>{$(x.neto)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ ...st.nota, padding: `${space.md}px ${space.xl}px`, margin: 0 }}>
        Control: bruto del informe − bruto del archivo = <b style={{ color: Math.abs(inf.total.descuadre) < 0.01 ? colors.ok : colors.warnStrong }}>$ {$(inf.total.descuadre)}</b>.
        {' '}Las retenciones y percepciones no son gasto: son crédito a computar.
      </p>
      {inf.cargos_de_servicio?.length > 0 && (
        <div style={{ padding: `${space.md}px ${space.xl}px ${space.xl}px` }}>
          <h3 style={sx.cardTitle}>Aparte: cargos sin ventas</h3>
          <p style={st.nota}>Payway los debita en liquidaciones sin cupones (por ejemplo, el cargo de estado de cuenta). No corresponden a ninguna venta, por eso no están arriba.</p>
          {inf.cargos_de_servicio.map((s) => (
            <div key={`${s.nro_liq}-${s.num_est}`} style={st.filaMeta}>
              Liq. {s.nro_liq} · est. {s.num_est} · {fmtFecha(s.fecha_pago)} · {Object.entries(s.cargos).map(([k, v]) => `${k} $ ${$(v)}`).join(' · ')}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Liquidaciones({ filas, verificada }) {
  return (
    <table style={sx.table}>
      <thead>
        <tr>
          <th style={sx.th}>Nro. LGR</th><th style={sx.th}>Liquidación</th><th style={sx.th}>Establec.</th><th style={sx.th}>Pago</th><th style={sx.th}>Marca</th>
          <th style={{ ...sx.th, textAlign: 'right' }}>Cupones</th><th style={{ ...sx.th, textAlign: 'right' }}>Bruto</th><th style={{ ...sx.th, textAlign: 'right' }}>Neto</th>
          <th style={sx.th}>Estado</th>{verificada && <th style={sx.th}>Presea</th>}
        </tr>
      </thead>
      <tbody>
        {filas.map((l) => (
          <tr key={l.id} style={st.tr}>
            <td style={{ ...st.td, ...sx.mono }}>{l.numero_lgr || '—'}</td>
            <td style={{ ...st.td, ...sx.mono }}>{l.nro_liq}</td>
            <td style={{ ...st.td, ...sx.mono }}>{l.num_est}</td>
            <td style={st.td}>{l.fecha_pago ? fmtFecha(l.fecha_pago) : '—'}</td>
            <td style={st.td}>{l.marca || '—'}</td>
            <td style={st.tdNum}>{l.apareados}/{l.cupones}</td>
            <td style={st.tdNum}>{$(l.bruto)}</td>
            <td style={st.tdNum}>{$(l.neto)}</td>
            <td style={st.td}>
              {l.completa
                ? <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }}>Completa</span>
                : <span style={st.motivo}>{l.motivo_incompleta}</span>}
            </td>
            {verificada && <td style={st.td}>{l.estado_presea === 'tomada' ? 'Tomada' : l.estado_presea === 'rechazada' ? <span style={{ color: colors.warnStrong, fontWeight: 600 }}>No la tomó</span> : '—'}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function SinPareja({ filas }) {
  if (filas.length === 0) return <div style={st.vacio}>Todos los cupones de Payway tienen su movimiento en Presea.</div>
  return (
    <table style={sx.table}>
      <thead>
        <tr>
          <th style={sx.th}>Compra</th><th style={sx.th}>Liquidación</th><th style={sx.th}>Establec.</th><th style={sx.th}>Autorización</th><th style={sx.th}>Cupón</th>
          <th style={sx.th}>Marca</th><th style={{ ...sx.th, textAlign: 'right' }}>Importe</th><th style={sx.th}>Por qué no se encontró</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((c) => (
          <tr key={c.id} style={st.tr}>
            <td style={st.td}>{fmtFecha(c.fecha_compra)}</td>
            <td style={{ ...st.td, ...sx.mono }}>{c.nro_liq}</td>
            <td style={{ ...st.td, ...sx.mono }}>{c.num_est}</td>
            <td style={{ ...st.td, ...sx.mono }}>{c.num_aut}</td>
            <td style={{ ...st.td, ...sx.mono }}>{c.num_comp}</td>
            <td style={st.td}>{c.marca || '—'}</td>
            <td style={st.tdNum}>{$(c.importe)}</td>
            <td style={{ ...st.td, ...st.motivo, minWidth: 340 }}>{c.motivo}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Pasadas({ filas }) {
  return (
    <div>
      <p style={{ ...st.nota, padding: `${space.lg}px ${space.xl}px`, margin: 0 }}>
        El cruce prueba combinaciones de la más segura a la más laxa y repite las barridas hasta que ninguna encuentra nada nuevo. Un cupón se aparea solo si tiene un único movimiento posible y ese movimiento, un único cupón: nunca se elige al azar.
      </p>
      <table style={sx.table}>
        <thead><tr><th style={sx.th}>Pasada</th><th style={sx.th}>Criterio</th><th style={{ ...sx.th, textAlign: 'right' }}>Cupones encontrados</th></tr></thead>
        <tbody>
          {filas.map((p) => (
            <tr key={p.pasada} style={st.tr}>
              <td style={{ ...st.td, ...sx.mono }}>{p.pasada}</td>
              <td style={st.td}>{p.regla}</td>
              <td style={st.tdNum}>{p.cupones}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const st = {
  wrap: { padding: space.xl, maxWidth: 1400, margin: '0 auto' },
  head: { display: 'flex', alignItems: 'baseline', gap: space.lg, flexWrap: 'wrap', marginBottom: space.lg },
  h1: { margin: 0, fontSize: text.title + 4, fontWeight: 600, color: colors.text },
  h2: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { fontSize: text.sec, color: colors.textSec },
  error: { background: colors.errBg, color: colors.err, border: `1px solid ${colors.errBorder}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, fontSize: text.body },
  aviso: { background: colors.okBg, color: colors.ok, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, fontSize: text.body },
  carga: { display: 'flex', gap: space.xl, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: space.lg },
  campo: { display: 'flex', flexDirection: 'column', gap: space.sm, minWidth: 0 },
  label: { fontSize: text.sec, fontWeight: 600, color: colors.textSub },
  nota: { fontSize: text.sec, color: colors.textSec, lineHeight: 1.5, margin: `${space.md}px 0 0` },
  cuerpo: { display: 'grid', gridTemplateColumns: 'minmax(220px, 260px) minmax(0, 1fr)', gap: space.xl },
  vacio: { padding: space.xl, color: colors.textMeta, fontSize: text.body },
  fila: { display: 'block', width: '100%', textAlign: 'left', background: colors.bg, border: 'none', borderTop: `1px solid ${colors.borderSoft}`, padding: `${space.md}px ${space.xl}px`, cursor: 'pointer', fontFamily: 'inherit' },
  filaSel: { background: colors.linkBg },
  filaTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, fontSize: text.body, fontWeight: 600, color: colors.text },
  filaMeta: { fontSize: text.sec, color: colors.textSec, marginTop: 2, lineHeight: 1.45 },
  detHead: { display: 'flex', justifyContent: 'space-between', gap: space.lg, flexWrap: 'wrap', alignItems: 'flex-start' },
  acciones: { display: 'flex', gap: space.sm, flexWrap: 'wrap', alignItems: 'center' },
  numeros: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: space.lg, marginTop: space.xl },
  numero: { borderTop: `2px solid ${colors.border}`, paddingTop: space.md },
  numeroN: { fontSize: 22, fontWeight: 600, color: colors.text, ...sx.mono },
  numeroL: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 },
  tabs: { display: 'flex', gap: space.xs, borderBottom: `1px solid ${colors.border}`, marginBottom: space.lg, flexWrap: 'wrap' },
  tab: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: `${space.md}px ${space.lg}px`, fontSize: text.body, color: colors.textSec, cursor: 'pointer', fontFamily: 'inherit' },
  tabOn: { color: colors.text, fontWeight: 600, borderBottomColor: colors.primary },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 36 },
  trTotal: { borderTop: `2px solid ${colors.border}`, background: colors.bgSoft, height: 38, fontWeight: 600 },
  td: { ...sx.td, whiteSpace: 'nowrap' },
  tdNum: { ...sx.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' },
  motivo: { fontSize: text.sec, color: colors.textSub, whiteSpace: 'normal', lineHeight: 1.35 },
  procesando: { marginTop: space.lg, background: colors.warnBg, color: colors.warn, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, fontSize: text.body },
}
